import { usePetBrushRoll } from "@/components/Cart/hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/utils/ShopifyHelpers";

type PetBrushRollUpsellProps = {
  onAddPetBrushRoll: () => Promise<void> | void;
  onInteractionStart?: ((target: HTMLButtonElement) => void) | undefined;
  isMutating?: boolean;
  quantity?: number;
};

const PET_OWNER_HELP =
  "Have pets? Felt-lined edges pick up more hair, while our bristle-free design means less wrapping around the roll. Ships preinstalled in your Matic instead of the standard brush roll.";

export default function PetBrushRollUpsell({
  onAddPetBrushRoll,
  onInteractionStart,
  isMutating = false,
  quantity = 1,
}: PetBrushRollUpsellProps) {
  const { unitPrice, currencyCode, title, isLoading } = usePetBrushRoll();

  const displayName = title ?? "Pet Brush Roll Upgrade";
  const displayPrice =
    unitPrice !== undefined && currencyCode
      ? formatPrice(unitPrice * quantity, currencyCode)
      : null;

  return (
    <div className="mt-4 flex w-full flex-col items-end">
      <div className="grid w-full grid-cols-[1fr_auto] gap-x-[16px] gap-y-[16px] rounded-[8px] border border-[#dbdbdb] p-[16px] [@media(min-width:475px)]:grid-cols-[1fr_auto_auto] [@media(min-width:475px)]:gap-x-[24px]">
        <span className="col-start-1 row-start-1 flex min-h-[36px] w-fit items-center rounded-[8px] bg-[#f5f3ef] px-[16px] py-[8px] font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#1f2022]">
          {displayName}
        </span>
        <span className="col-start-2 row-start-1 self-center font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#1f2022]">
          {displayPrice ? (
            `+ ${displayPrice}`
          ) : isLoading ? (
            <Skeleton className="h-[18px] w-[64px] rounded-[4px] bg-[#dbdbdb]" />
          ) : null}
        </span>
        <button
          type="button"
          aria-label={`Add ${displayName}${displayPrice ? ` for ${displayPrice}` : ""}`}
          onClick={(event) => {
            onInteractionStart?.(event.currentTarget);
            void onAddPetBrushRoll();
          }}
          disabled={isMutating}
          className="col-start-2 row-start-4 flex h-[36px] w-[79px] shrink-0 items-center justify-center self-end rounded-[50px] border border-black bg-[#1f2022] px-0 font-trimMono text-[16px] uppercase leading-[1.6] tracking-[0.48px] text-white disabled:cursor-not-allowed disabled:opacity-50 [@media(min-width:475px)]:col-start-3 [@media(min-width:475px)]:row-start-1 [@media(min-width:475px)]:self-center"
        >
          Add
        </button>
        <div className="col-span-2 row-start-2 h-px w-full bg-[#dbdbdb] [@media(min-width:475px)]:col-span-3" />
        <p className="col-span-2 row-start-3 font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#54524e] [@media(min-width:475px)]:col-span-3">
          {PET_OWNER_HELP}
        </p>
      </div>
    </div>
  );
}
